<template>
  <v-app width="100%" height="100%">
    <core-offline v-if="offline"></core-offline>
    <template v-else-if="isLoggedIn && hasHeader">
      <to-do-drawer v-if="!isAdminSection && !hasRole(user, 'Parent')" :active="toDoDrawerActive" @active="(val) => toDoDrawerActive = val"></to-do-drawer>
      <transition name="slide-down">
        <core-header class="no-print" @openToDoList="toDoDrawerActive = !toDoDrawerActive"></core-header>
      </transition>
    </template>
    <v-img :src="imageSource" max-width="100%" max-height="100%" class="main-image">
      <core-view v-if="!offline && (isLoggedIn || isParentProfile || isAccountSetup)"></core-view>
      <template v-else-if="!offline && !isLoggedIn">
        <core-login :messages="messages" @loginSuccess="resetLoginTimer"></core-login>
        <div class='footer'>
          This site is reserved for use by Covenant College employees, students, and other designated individuals. Use of this resource without authority, or in excess of your authority, is strictly prohibited. Continued use of this site constitutes agreement with the terms of usage outlined by Covenant College.
        </div>
      </template>
    </v-img>
  </v-app>
</template>
<style>
  .v-snack--top {
    top: 70px;
  }
  .dark-background {
    width: 100%;
    height: 100%;
    background-color: #193264;
    opacity: .4;
  }
  .footer {
    position: fixed;
    bottom: 0;
    background-color: black;
    color: white;
    text-align: center;
    padding: 1em 3em;
    width: 100%;
    /* opacity: 1; */
    /* z-index: 100; */
  }
  .theme--dark.v-btn:not(.v-btn--outlined).primary, .theme--dark.v-btn:not(.v-btn--outlined).secondary, .theme--dark.v-btn:not(.v-btn--outlined).accent, .theme--dark.v-btn:not(.v-btn--outlined).success, .theme--dark.v-btn:not(.v-btn--outlined).error, .theme--dark.v-btn:not(.v-btn--outlined).warning, .theme--dark.v-btn:not(.v-btn--outlined).info {
    color: black !important;
  }
  .main-image > div.v-image__image {
    background-attachment: fixed;
  }
  @media print {
    .no-print {
      display: none !important;
    }
    main.v-main {
      padding: 0 !important;
    }
    div#formContainer div.row > div {
      page-break-inside: avoid;
    }
  }
  .mini-scrollbar::-webkit-scrollbar {
    width: 5px;
    height: 11px;
  }
  .mini-scrollbar::-webkit-scrollbar-button {
    width: 5px;
    height: 0px;
  }
  .mini-scrollbar::-webkit-scrollbar-corner {
    background: transparent;
  }
  .mini-scrollbar::-webkit-scrollbar-thumb {
    border: 0px solid transparent;
    border-radius: 50px;
  }
  .theme--light .mini-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(to top right, #193264, #003366);
  }
  .theme--dark .mini-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(to top right, #c0c0c0, #a0a0a0);
  }
  .mini-scrollbar::-webkit-scrollbar-track {
    border: 0px none;
    border-radius: 53px;
  }
  .v-btn--active.no-active::before {
    opacity: 0 !important
  }
  div.v-card__text h1, div.v-card__text h2, div.v-card__text h3 {
    margin-bottom: .5em;
  }
  .theme--light.v-label, .theme--light.v-subheader, .theme--light.v-list-item .v-list-item__subtitle, .theme--light.v-list-item .v-list-item__action-text, .theme--light.v-card .v-card__subtitle, .theme--light.v-card>.v-card__text {
    color: rgba(0,0,0,1) !important
  }
  .theme--dark.v-label, .theme--dark.v-subheader, .theme--dark.v-list-item .v-list-item__subtitle, .theme--dark.v-list-item .v-list-item__action-text, .theme--dark.v-card .v-card__subtitle, .theme--dark.v-card>.v-card__text {
    color: rgba(255, 255, 255, 1) !important
  }
  h1 {
    line-height: 1em;
  }

  .slide-down-fade-enter-active, .slide-down-fade-leave-active {
    transition: all .7s ease;
  }
  .slide-down-fade-enter, .slide-down-fade-leave-to {
    transform: translateY(-300px);
    opacity: 0;
  }
  .slide-down-fade-enter-to, .slide-down-fade-leave {
    transform: translateY(0);
    opacity: 1;
  }

  .slide-down-enter-active, .slide-down-leave-active {
    transition: all 7s ease;
  }
  .slide-down-enter, .slide-down-leave-to {
    transform: translateY(-64px);
  }
  .slide-down-enter-to, .slide-down-leave {
    transform: translateY(0);
  }
  .v-application .v-alert p {
    margin-bottom: 0;
  }
  .v-image__image.v-image__image--cover {
    transition: background .5s linear;
  }
</style>
<script>
import { hasRole } from './helpers/has'
import { ref, computed, watch, onBeforeMount } from '@vue/composition-api'

export default {
  name: 'App',
  components: {
    CoreOffline: () => import('@/components/core/Offline'),
    CoreHeader: () => import(/* webpackChunkName: "core" */ '@/components/core/Header'),
    CoreView: () => import(/* webpackChunkName: "core" */ '@/components/core/View'),
    CoreLogin: () => import(/* webpackChunkName: "core" */ '@/components/core/Login'),
    ToDoDrawer: () => import('@/components/core/ToDoDrawer')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const offline = computed(() => root.$store.state.offline)
    const imageSource = ref('')
    const imageTimer = ref(null)
    const isLoggedIn = computed(() => root.$store.state.loggedIn)
    const messages = ref([])
    const checklist = computed(() => root.$store.state.checklist)

    const isParentProfile = computed(() => root.$route.path.substring(0, 8) === '/profile')
    const isAccountSetup = computed(() => root.$route.path.substring(0, 6) === '/setup')

    watch(isLoggedIn, (val) => {
      if (!val) {
        let num = (Math.floor(Math.random() * 100) % 33)
        imageSource.value = require('@/assets/backgrounds/background' + (num < 10 ? '0' : '') + num + '.jpg')
      } else {
        let pathParts = root.$route.path.substr(1).split('/')
        if (!isLoggedIn.value || pathParts[0] === '' || (pathParts[0] === 'action' && (pathParts.length === 1 || pathParts[1] !== 'pre-register')) || (pathParts[0] === 'admin' && (pathParts.length === 1 || pathParts[1] !== 'preview'))) {
          // Check to see if the oldParts would have been an image background
          if (imageSource.value === require('@/assets/background.png') || imageSource.value === '' || imageTimer.value < new Date()) {
            // If not, then set the imageSource to a random background image
            let num = (Math.floor(Math.random() * 100) % 33)
            imageSource.value = require('@/assets/backgrounds/background' + (num < 10 ? '0' : '') + num + '.jpg')
            let dt = new Date()
            dt.setMinutes(dt.getMinutes() + 5)
            imageTimer.value = dt
          }
          return
        }
        imageSource.value = require('@/assets/background.png')
      }
    })
    watch(() => root.$route, (val) => {
      if (val) {
        let pathParts = val.path.substr(1).split('/')
        if (!isLoggedIn.value || pathParts[0] === '' || (pathParts[0] === 'action' && (pathParts.length === 1 || pathParts[1] !== 'pre-register')) || (pathParts[0] === 'admin' && (pathParts.length === 1 || pathParts[1] !== 'preview'))) {
          // Check to see if the oldParts would have been an image background
          if (imageSource.value === require('@/assets/background.png') || imageSource.value === '' || imageTimer.value < new Date()) {
            // If not, then set the imageSource to a random background image
            let num = (Math.floor(Math.random() * 100) % 33)
            imageSource.value = require('@/assets/backgrounds/background' + (num < 10 ? '0' : '') + num + '.jpg')
            let dt = new Date()
            dt.setMinutes(dt.getMinutes() + 5)
            imageTimer.value = dt
          }
          return
        }
        imageSource.value = require('@/assets/background.png')
      }
    })
    const darkMode = computed(() => root.$store.state.settings.darkMode || false)
    watch(darkMode, () => {
      root.$vuetify.theme.dark = darkMode.value
    })
    const isRoot = computed(() => root.$route.path === '/')

    const hasHeader = computed(() => {
      if (isRoot.value) return true
      else if (root.$route.path.toLowerCase().substr(0, 7) === '/action') return true
      else if (root.$route.path.toLowerCase().substr(0, 6) === '/admin') return true
      return false
    })
    const isAdminSection = computed(() => {
      return root.$route.path.toLowerCase().substr(0, 6) === '/admin'
    })

    if (isLoggedIn.value) root.$store.commit('updateActivity')
    root.$vuetify.theme.dark = darkMode.value || false

    function resetLoginTimer () {
      root.$store.commit('updateActivity')
    }

    onBeforeMount(() => {
      root.$store.commit('setReferrer', document.referrer)
      if (isLoggedIn.value) root.$store.dispatch('loadChecklist')
    })

    const toDoDrawerActive = ref(false)

    return {
      user,
      offline,
      imageSource,
      darkMode,
      hasHeader,
      isAdminSection,
      isRoot,
      isLoggedIn,
      messages,
      checklist,
      isParentProfile,
      isAccountSetup,
      resetLoginTimer,
      toDoDrawerActive,
      hasRole
    }
  }
}
</script>
